import { useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { nanoid } from '@reduxjs/toolkit';
import { Alert, Button, Card, Col, Row, Skeleton, Space } from 'antd';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';

import { DataForm, FieldProps, FieldType } from 'components';
import AddressAttribution from 'components/DataForm/components/AddressAttribution';
import ScannerModal from 'components/DataForm/components/ScannerModal';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { useCreateLoggerProjectMutation, useGetLoggerCountriesQuery, useLazyGetLoggerAddressesQuery, useLazyGetLoggerAreasQuery, useLazyGetLoggerOverviewQuery, useLazyGetLoggerProjectToolsQuery, useLazyGetLoggerWorkNamesQuery } from 'app/services/loggers';
import { actions as loggerAccess, selectors as loggerAccessSelectors } from 'app/services/loggers/slice';
import { AreaOption, StreetOption } from 'app/services/addresses/types';
import { ToolOption } from 'app/services/loggers/types';
import { QuickSearch } from 'app/types/search';
import { Paths } from 'constants/index';
import { useRevokeOnHistoryNavigation, useScreenBreakpoint } from 'hooks';
import { parseQrValue } from 'utils/qr/parseQrValue';
import './overview.less';

const FORM_ID = 'loggerworkplace.create';

const DEFAULT_LIMITS = { LowLimit: -40, HighLimit: -10, Frequency: 60 };

type ToolChoice = { label: string, value: number, qrCode: string };

const toChoices = (tools: ToolOption[]): ToolChoice[] => tools.map((tool) => ({ label: tool.Label, value: tool.Id, qrCode: tool.QrCode }));

const NewWorkplace = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isMobile } = useScreenBreakpoint();
  const { barcode = '' } = useParams<{ barcode: string }>();
  const access = useAppSelector(loggerAccessSelectors.getLoggerAccess(barcode));
  const viaHistory = useRevokeOnHistoryNavigation();

  const [ createProject, meta ] = useCreateLoggerProjectMutation();
  const [ getOverview ] = useLazyGetLoggerOverviewQuery();
  const [ getWorkNames ] = useLazyGetLoggerWorkNamesQuery();
  const [ workNames, setWorkNames ] = useState<QuickSearch[]>([]);
  const [ getAreas, { isFetching: areasLoading } ] = useLazyGetLoggerAreasQuery();
  const [ areas, setAreas ] = useState<AreaOption[]>([]);
  const [ selectedArea, setSelectedArea ] = useState<{ name?: string, filter?: string }>({});
  const [ getAddresses, { isFetching: addressesLoading } ] = useLazyGetLoggerAddressesQuery();
  const [ addresses, setAddresses ] = useState<StreetOption[]>([]);
  const [ getTools, { data: tools } ] = useLazyGetLoggerProjectToolsQuery();
  const [ toolChoices, setToolChoices ] = useState<ToolChoice[]>([]);
  const { data: countryData, isLoading: countriesLoading } = useGetLoggerCountriesQuery({ barcode, code: access?.code ?? '' }, { skip: !access });
  const [ countryId, setCountryId ] = useState<number>();
  const submitting = useRef(false);
  const submissionKey = useRef(nanoid());

  const countries = countryData?.CountryList;
  const defaultCountryId = countryData?.ClientCountryId ?? countries?.[0]?.Id;
  const selectedCountryId = countryId ?? defaultCountryId ?? 0;

  useEffect(() => {
    if (!access) return;
    getTools({ barcode, code: access.code });
  }, []);

  // Work names are per country.
  useEffect(() => {
    if (!access || !selectedCountryId) return;
    getWorkNames({ barcode, code: access.code, countryId: selectedCountryId }).unwrap().then(setWorkNames).catch(() => setWorkNames([]));
  }, [ selectedCountryId ]);

  useEffect(() => {
    if (tools) setToolChoices(toChoices(tools));
  }, [ tools ]);

  const codePath = Paths.LOGGERS_OVERVIEW_CODE.replace(':barcode', encodeURIComponent(barcode));
  const overviewPath = Paths.LOGGERS_OVERVIEW.replace(':barcode', encodeURIComponent(barcode));

  if (!access || viaHistory) return <Navigate to={codePath} replace />;

  const current = access.overview.HasActiveProject ? access.overview.Project : undefined;

  const searchTools = (value: string) => {
    const all = toChoices(tools ?? []);
    const term = value.toLowerCase();
    // The QR code is not in the label, but a typed one finds the tool too.
    setToolChoices(value ? all.filter((tool) => tool.label.toLowerCase().includes(term) || (tool.qrCode ?? '').toLowerCase().includes(term)) : all);
  };

  const scannerSearchTools = (scanned: string) => {
    const { barcodeValue } = parseQrValue(scanned);
    const match = toChoices(tools ?? []).find((choice) => choice.qrCode === barcodeValue);
    return match ? [ match ] : [];
  };

  const handleCountryChange = (id: number) => {
    setCountryId(id);
    setSelectedArea({});
    setAreas([]);
    setAddresses([]);
  };

  // A picked street carries its municipality.
  const handleStreetSelect = (option?: StreetOption) => {
    if (option?.area) setSelectedArea({ name: option.area });

    return { AreaName: option?.area };
  };

  const fields: FieldProps[] = [
    {
      name: 'CountryId',
      label: t('entities.site.countryid'),
      required: true,
      type: FieldType.SELECT,
      selectProps: { options: countries, labelKey: 'LocalName', valueKey: 'Id' },
      initialValue: defaultCountryId,
      onChange: handleCountryChange,
      // Suggestions belong to a country: changing it clears the picked area and street.
      setFields: { keys: [ 'AreaName', 'Address' ], onSelect: () => ({}) }
    },
    {
      name: 'AreaName',
      label: t('entities.site.areaname'),
      required: true,
      max: 128,
      type: FieldType.QUICK_SEARCH,
      searchProps: {
        searchCallback: (search: string) => getAreas({ barcode, code: access.code, search, countryId: selectedCountryId }),
        setDataCallback: setAreas,
        options: areas,
        minLength: 3
      },
      state: { loading: areasLoading },
      onChange: (name: string, option?: AreaOption) => setSelectedArea({ name, filter: option?.filter })
    },
    {
      name: 'Address',
      label: t('entities.site.address'),
      required: true,
      max: 512,
      type: FieldType.QUICK_SEARCH,
      searchProps: {
        searchCallback: (search: string) => getAddresses({ barcode, code: access.code, search, countryId: selectedCountryId, area: selectedArea.name, areaFilter: selectedArea.filter }),
        setDataCallback: setAddresses,
        options: addresses,
        minLength: 3
      },
      state: { loading: addressesLoading },
      setFields: { keys: [ 'AreaName' ], onSelect: handleStreetSelect, skipUndefined: true },
      extra: <AddressAttribution />
    },
    {
      name: 'Building',
      label: t('entities.site.building'),
      max: 256,
      noSpaces: true,
    },
    { name: 'WorkPlace', required: true, max: 1024 },
    {
      name: 'WorkName',
      required: true,
      max: 1024,
      type: FieldType.QUICK_SEARCH,
      searchProps: {
        searchCallback: (search: string) => getWorkNames({ barcode, code: access.code, countryId: selectedCountryId, search }),
        setDataCallback: setWorkNames,
        options: workNames
      },
      allowEmptySearch: true
    },
    { name: 'WorkerName', required: true, max: 512, capitalize: true },
    {
      name: 'ToolIds',
      type: FieldType.SELECT_SEARCH,
      searchProps: {
        options: toolChoices,
        multiple: true,
        searchCallback: searchTools,
        setDataCallback: undefined,
        localFilter: true,
      },
      allowEmptySearch: true,
      addOn: (
        <ScannerModal
          searchCallBack={scannerSearchTools}
          transformValue={(scanned) => parseQrValue(scanned).barcodeValue}
          local
          multiple
        />
      )
    },
    { name: 'HighLimit', required: true, type: FieldType.NUMBER, numberProps: { min: -32768, max: 32767, precision: 0, controls: false } },
    { name: 'LowLimit', required: true, type: FieldType.NUMBER, numberProps: { min: -32768, max: 32767, precision: 0, controls: false } },
    { name: 'Frequency', required: true, type: FieldType.NUMBER, numberProps: { min: 1, max: 32767, precision: 0, controls: false } }
  ];

  const handleSubmit = async (values: any) => {
    if (submitting.current) return;
    submitting.current = true;

    try {
      await createProject({
        barcode,
        code: access.code,
        submissionKey: submissionKey.current,
        project: {
          CountryId: values.CountryId,
          AreaName: values.AreaName,
          Address: values.Address,
          Building: values.Building,
          WorkPlace: values.WorkPlace,
          WorkName: values.WorkName,
          WorkerName: values.WorkerName,
          LowLimit: values.LowLimit,
          HighLimit: values.HighLimit,
          Frequency: values.Frequency,
          ToolIds: values.ToolIds ?? []
        }
      }).unwrap();

      const overview = await getOverview({ barcode, code: access.code }).unwrap();
      dispatch(loggerAccess.updateOverview(overview));
      navigate(overviewPath, { replace: true });
    } catch (error) {
      const status = (error as { status?: number | string } | undefined)?.status;
      if (status === 409) {
        const overview = await getOverview({ barcode, code: access.code }).unwrap().catch(() => undefined);
        if (overview) dispatch(loggerAccess.updateOverview(overview));
        navigate(overviewPath, { replace: true });
        return;
      }

      if (status === 400) {
        dispatch(loggerAccess.revoke());
        navigate(codePath, { replace: true });
        return;
      }
    } finally {
      submitting.current = false;
    }
  };

  return (
    <>
      <Helmet title={`Kvalilog > ${t('pages.loggers.workplace.addnew')}`} />
      <Row className="logger-workplace-form">
        <Col span={24}>
          <Card title={t('pages.loggers.workplace.addnew')}>
            {current && (
              <Alert
                className="logger-form-warning"
                type="warning"
                showIcon
                message={t('pages.loggers.workplace.addnewwarning', { workplace: current.WorkPlace })}
              />
            )}
            {countriesLoading ? <Skeleton active /> : (
              <DataForm
                id={FORM_ID}
                entity="project"
                onSubmit={handleSubmit}
                fields={fields}
                meta={meta}
                initialValues={DEFAULT_LIMITS}
                noSuggestions
                successMsg={t('pages.loggers.workplace.created')}
              />
            )}
            <Space direction={isMobile ? 'vertical' : 'horizontal'} className="logger-form-actions">
              <Button form={FORM_ID} htmlType="submit" type="primary" loading={meta.isLoading} block={isMobile}>
                {t('common.save')}
              </Button>
              {current && (
                <Button onClick={() => navigate(overviewPath, { replace: true })} disabled={meta.isLoading} block={isMobile}>
                  {t('common.cancel')}
                </Button>
              )}
            </Space>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default NewWorkplace;
